// Locating a finding in the document, and quoting it verbatim.
//
// Every quote a check emits is sliced out of doc.rawText by index, so it is a
// verbatim substring by construction rather than by hope. The orchestrator
// re-validates by substring match anyway and drops anything that fails.

import type { QuoteOffsets, Section, SourceDocument } from '../../types';

const PAGE_JOINER = '\n\n';

export interface Locator {
  /** 1-based page number containing this character index. */
  pageOf(index: number): number | null;
  /** The section whose text contains this index, preferring the deepest match. */
  sectionOf(index: number): Section | null;
  /**
   * A verbatim window around an index, trimmed outward to line boundaries so
   * the quote reads as whole lines rather than clipped words, and never past
   * the ends of the page the match is on.
   */
  quoteAround(index: number, length: number, radius?: number): string;
  /**
   * Where a rawText span sits on its own page, for the document pane's
   * highlight. Null when the span falls outside every page or runs across a
   * page break, since neither can be shown as one highlight on one page.
   */
  offsetsAt(start: number, end: number): QuoteOffsets | null;
  /**
   * The same, for a quote whose position is not already known. `nearIndex`
   * disambiguates a quote that occurs more than once. Null when the quote is
   * absent or cannot be found — the pane then says so rather than highlighting
   * a span it guessed at.
   */
  offsetsOf(quote: string | null, nearIndex?: number): QuoteOffsets | null;
}

export function buildLocator(doc: SourceDocument): Locator {
  // Page boundaries within rawText, mirroring how ingest joined the pages.
  const bounds: { page: number; start: number; end: number }[] = [];
  let cursor = 0;
  for (const page of doc.pages) {
    const start = cursor;
    const end = start + page.text.length;
    bounds.push({ page: page.number, start, end });
    cursor = end + PAGE_JOINER.length;
  }

  // Deepest section first, so 3.2.1 wins over 3.2 for the same text.
  const sectionsByDepth = [...doc.sections].sort(
    (a, b) => b.number.split('.').length - a.number.split('.').length,
  );

  return {
    pageOf(index) {
      const hit = bounds.find((b) => index >= b.start && index <= b.end);
      return hit ? hit.page : null;
    },

    sectionOf(index) {
      const page = this.pageOf(index);
      // Take a short signature from rawText and find which section text holds it.
      const signature = doc.rawText.slice(index, index + 40);
      if (signature.trim()) {
        for (const section of sectionsByDepth) {
          if (section.text.includes(signature)) return section;
        }
      }
      if (page === null) return null;
      // Fall back to the last section that had started by this page.
      const candidates = doc.sections.filter((s) => s.startPage <= page);
      return candidates.length ? candidates[candidates.length - 1] : null;
    },

    quoteAround(index, length, radius = 180) {
      const text = doc.rawText;

      // The window is confined to the page the match is on. A window that ran
      // over a page break would silently join the foot of one page to the head
      // of the next — running headers, page numbers and all — and the result
      // could not be highlighted anywhere, because a span that sits on two
      // pages sits on no single page the document pane can show. Confining it
      // makes both the quote and the span honest. See docs/redesign/REPORT.md.
      const home = bounds.find((b) => index >= b.start && index <= b.end);
      const floor = home ? home.start : 0;
      const ceiling = home ? home.end : text.length;

      let start = Math.max(floor, index - radius);
      let end = Math.min(ceiling, index + length + radius);

      // Grow outward to the enclosing line breaks, but never past the page.
      const prevBreak = text.lastIndexOf('\n', start);
      if (prevBreak !== -1 && prevBreak + 1 > floor) start = prevBreak + 1;
      const nextBreak = text.indexOf('\n', end);
      if (nextBreak !== -1 && nextBreak < ceiling) end = nextBreak;

      return text.slice(start, end).replace(/^\s+|\s+$/g, '');
    },

    offsetsAt(start, end) {
      if (!(end > start)) return null;
      const hit = bounds.find((b) => start >= b.start && start < b.end);
      if (!hit) return null;
      // A span running past the end of its page has no single page to sit on.
      if (end > hit.end) return null;
      return { page: hit.page, start: start - hit.start, end: end - hit.start };
    },

    offsetsOf(quote, nearIndex) {
      if (!quote) return null;
      const text = doc.rawText;
      let at = -1;
      if (nearIndex !== undefined) {
        at = text.indexOf(quote, Math.max(0, nearIndex - quote.length));
      }
      if (at < 0) at = text.indexOf(quote);
      if (at < 0) return null;
      return this.offsetsAt(at, at + quote.length);
    },
  };
}

/** How a section should be cited in a flag. */
export function sectionRefOf(section: Section | null): string {
  if (!section) return 'Document';
  if (!section.number) return section.title || 'Document';
  return `§${section.number} ${section.title}`.trim();
}

/** Sections making up one ECM: the top-level section and all its descendants. */
export function ecmScopes(doc: SourceDocument): {
  ref: string;
  sections: Section[];
  text: string;
}[] {
  const tops = doc.sections.filter(
    (s) => s.number.split('.').length === 1 && /\bECM\b/i.test(s.title),
  );

  return tops.map((top) => {
    const sections = doc.sections.filter(
      (s) => s.number === top.number || s.number.startsWith(`${top.number}.`),
    );
    return {
      ref: `§${top.number} ${top.title}`,
      sections,
      text: sections.map((s) => s.text).join('\n'),
    };
  });
}
